import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { STATUS_COLORS, STATUS_LABELS } from './BidInvitations';
import { useIsMobile } from '../../hooks/useIsMobile';
import { fmtDate, daysFromNowCST } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
const fmt = fmtDate;

const BID_PIPELINE = ['invited', 'reviewing_documents', 'takeoff_in_progress', 'pricing_in_progress', 'bid_ready', 'bid_submitted', 'clarification', 'negotiation', 'awaiting_award'];
const TERMINAL = ['awarded', 'lost', 'no_bid', 'cancelled'];
const DOC_TYPE_LABELS = { plans: 'Plans', specifications: 'Specifications', addendum: 'Addendum', bid_form: 'Bid Form', scope_of_work: 'Scope of Work', contract: 'Contract', insurance_req: 'Insurance Req', other: 'Other' };
const CONFIDENCE_COLORS = { CONFIRMED_FROM_DOCUMENT: '#16a34a', INFERRED: '#f59e0b', MISSING_NEEDS_REVIEW: '#ef4444' };

export default function BidDetail() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [bid, setBid] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [showAward, setShowAward] = useState(false);
  const [showLose, setShowLose] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showAddRfi, setShowAddRfi] = useState(false);
  const [showExtract, setShowExtract] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [showEditBid, setShowEditBid] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bidRes, docsRes, rfiRes] = await Promise.all([
        fetch(`${API}/api/sales/commercial/bids/${bidId}`, authOpts()),
        fetch(`${API}/api/sales/commercial/bids/${bidId}/documents`, authOpts()),
        fetch(`${API}/api/sales/commercial/bids/${bidId}/clarifications`, authOpts()),
      ]);
      if (bidRes.ok) setBid(await bidRes.json());
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (rfiRes.ok) setClarifications(await rfiRes.json());
    } finally { setLoading(false); }
  }, [bidId]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (newStatus, notes = '') => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/status`, authOpts({
      method: 'POST', body: JSON.stringify({ status: newStatus, notes }),
    }));
    if (res.ok) { load(); setShowStatusChange(false); }
  };

  const submitBid = async (data) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/submit`, authOpts({
      method: 'POST', body: JSON.stringify(data),
    }));
    if (res.ok) { load(); setShowSubmit(false); }
  };

  const awardBid = async (data) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/award`, authOpts({
      method: 'POST', body: JSON.stringify(data),
    }));
    if (res.ok) { load(); setShowAward(false); }
  };

  const loseBid = async (data) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/lose`, authOpts({
      method: 'POST', body: JSON.stringify(data),
    }));
    if (res.ok) { load(); setShowLose(false); }
  };

  const addDocument = async (data) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/documents`, authOpts({
      method: 'POST', body: JSON.stringify(data),
    }));
    if (res.ok) { load(); setShowAddDoc(false); }
  };

  const addRfi = async (data) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}/clarifications`, authOpts({
      method: 'POST', body: JSON.stringify(data),
    }));
    if (res.ok) { load(); setShowAddRfi(false); }
  };

  const runExtraction = async (docId, textContent) => {
    setExtracting(true);
    try {
      await fetch(`${API}/api/sales/commercial/bids/${bidId}/extract`, authOpts({
        method: 'POST', body: JSON.stringify({ doc_id: docId, text_content: textContent }),
      }));
      load();
    } finally { setExtracting(false); setShowExtract(false); }
  };

  const updateBid = async (updates) => {
    const res = await fetch(`${API}/api/sales/commercial/bids/${bidId}`, authOpts({
      method: 'PUT', body: JSON.stringify(updates),
    }));
    if (res.ok) { load(); setShowEditBid(false); }
  };

  if (loading || !bid) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading bid...</div>;

  const statusColor = STATUS_COLORS[bid.bid_status] || '#94a3b8';
  const isTerminal = TERMINAL.includes(bid.bid_status);
  const daysLeft = bid.bid_due_date ? daysFromNowCST(bid.bid_due_date) : null;
  const pendingRfis = clarifications.filter(c => c.status === 'pending');

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'documents', label: `Documents (${documents.length})` },
    { key: 'extraction', label: 'AI Extraction' },
    { key: 'takeoff', label: 'Takeoff & Pricing' },
    { key: 'clarifications', label: `RFIs (${clarifications.length})` },
    { key: 'history', label: 'History' },
  ];

  return (
    <div data-testid="bid-detail" style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '20px 28px' }}>
      {/* Back */}
      <button data-testid="back-to-bids" onClick={() => navigate('/sales/portal')}
        style={{ marginBottom: 12, padding: '6px 12px', fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', minHeight: 36 }}>
        &larr; Back to Sales Portal
      </button>

      {/* Header Card */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>{bid.project_name}</h1>
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: `${statusColor}18`, color: statusColor, fontWeight: 700 }}>
                {STATUS_LABELS[bid.bid_status] || bid.bid_status}
              </span>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 4px' }}>
              <span onClick={() => navigate(`/sales/commercial/accounts/${bid.account_id}`)} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>{bid.company_name}</span>
              {bid.project_number ? ` — Project #${bid.project_number}` : ''}
            </p>
            {bid.bid_due_date && (
              <p style={{ fontSize: 13, fontWeight: 600, color: daysLeft !== null && daysLeft < 0 ? '#ef4444' : daysLeft !== null && daysLeft <= 3 ? '#f59e0b' : '#475569', margin: '4px 0' }}>
                Due: {fmt(bid.bid_due_date)}{bid.bid_due_time ? ` at ${bid.bid_due_time}` : ''}
                {daysLeft !== null && (daysLeft < 0 ? ' (OVERDUE)' : daysLeft === 0 ? ' (TODAY)' : ` (${daysLeft}d left)`)}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {bid.prevailing_wage && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>PREVAILING WAGE</span>}
              {bid.bond_required && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>BOND REQUIRED</span>}
              {bid.plans_received && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>PLANS</span>}
              {bid.specifications_received && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>SPECS</span>}
            </div>
          </div>
          {/* Quick Action Buttons */}
          {!isTerminal && (
            <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: 8, flexWrap: 'wrap' }}>
              <button data-testid="bid-edit-btn" onClick={() => setShowEditBid(true)}
                style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', minHeight: 40 }}>
                Edit
              </button>
              <button data-testid="bid-status-btn" onClick={() => setShowStatusChange(true)}
                style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', minHeight: 40 }}>
                Change Status
              </button>
              {bid.bid_status === 'bid_ready' && (
                <button data-testid="bid-submit-btn" onClick={() => setShowSubmit(true)}
                  style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', minHeight: 40 }}>
                  Submit Bid
                </button>
              )}
              {['bid_submitted', 'awaiting_award', 'negotiation'].includes(bid.bid_status) && (
                <>
                  <button data-testid="bid-award-btn" onClick={() => setShowAward(true)}
                    style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', minHeight: 40 }}>
                    Award
                  </button>
                  <button data-testid="bid-lose-btn" onClick={() => setShowLose(true)}
                    style={{ padding: '8px 14px', fontSize: 12, fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', minHeight: 40 }}>
                    Lost
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Status Pipeline */}
        {!isMobile && (
          <div style={{ marginTop: 16, display: 'flex', gap: 2, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
            {BID_PIPELINE.map((s, i) => {
              const isCurrent = bid.bid_status === s;
              const isPast = BID_PIPELINE.indexOf(bid.bid_status) > i;
              return (
                <div key={s} style={{ flex: 1, textAlign: 'center', padding: '6px 2px', borderRadius: 4,
                  background: isCurrent ? `${STATUS_COLORS[s]}18` : isPast ? '#f0fdf4' : '#f8fafc',
                  border: isCurrent ? `2px solid ${STATUS_COLORS[s]}` : '1px solid #e2e8f0',
                }}>
                  <span style={{ fontSize: 9, fontWeight: isCurrent ? 700 : 500, color: isCurrent ? STATUS_COLORS[s] : isPast ? '#16a34a' : '#94a3b8' }}>
                    {STATUS_LABELS[s]}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile Quick Actions */}
      {isMobile && !isTerminal && (
        <div data-testid="bid-mobile-actions" style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 14, padding: '0 2px' }}>
          <button onClick={() => setShowAddDoc(true)} style={mobileActionBtn('#6366f1')}>+ Doc</button>
          <button onClick={() => setShowAddRfi(true)} style={mobileActionBtn('#ec4899')}>+ RFI</button>
          <button onClick={() => setShowExtract(true)} style={mobileActionBtn('#f59e0b')}>AI Extract</button>
          <button onClick={() => setShowStatusChange(true)} style={mobileActionBtn('#3b82f6')}>Status</button>
          {bid.bid_status === 'bid_ready' && <button onClick={() => setShowSubmit(true)} style={mobileActionBtn('#14b8a6')}>Submit</button>}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, overflowX: 'auto', marginBottom: 16, borderBottom: '2px solid #e2e8f0', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`bid-tab-${t.key}`} onClick={() => setTab(t.key)}
            style={{
              padding: '10px 14px', fontSize: 12, fontWeight: tab === t.key ? 700 : 500, border: 'none', borderBottom: tab === t.key ? '2px solid #1e293b' : '2px solid transparent',
              color: tab === t.key ? '#1e293b' : '#64748b', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && <OverviewTab bid={bid} isMobile={isMobile} />}
      {tab === 'documents' && <DocumentsTab documents={documents} onAdd={() => setShowAddDoc(true)} onDelete={async (id) => {
        await fetch(`${API}/api/sales/commercial/bids/${bidId}/documents/${id}`, authOpts({ method: 'DELETE' }));
        load();
      }} isMobile={isMobile} />}
      {tab === 'extraction' && <ExtractionTab bid={bid} documents={documents} onExtract={(docId) => { setShowExtract(docId); }} extracting={extracting} isMobile={isMobile} />}
      {tab === 'takeoff' && <TakeoffTab bid={bid} onUpdate={updateBid} isMobile={isMobile} />}
      {tab === 'clarifications' && <ClarificationsTab clarifications={clarifications} onAdd={() => setShowAddRfi(true)} bidId={bidId} onReload={load} isMobile={isMobile} />}
      {tab === 'history' && <HistoryTab bid={bid} isMobile={isMobile} />}

      {/* Modals */}
      {showStatusChange && <StatusChangeModal bid={bid} onSubmit={changeStatus} onClose={() => setShowStatusChange(false)} isMobile={isMobile} />}
      {showSubmit && <SubmitBidModal bid={bid} onSubmit={submitBid} onClose={() => setShowSubmit(false)} isMobile={isMobile} />}
      {showAward && <AwardModal onSubmit={awardBid} onClose={() => setShowAward(false)} isMobile={isMobile} />}
      {showLose && <LoseModal onSubmit={loseBid} onClose={() => setShowLose(false)} isMobile={isMobile} />}
      {showAddDoc && <AddDocModal onSubmit={addDocument} onClose={() => setShowAddDoc(false)} isMobile={isMobile} />}
      {showAddRfi && <AddRfiModal onSubmit={addRfi} onClose={() => setShowAddRfi(false)} isMobile={isMobile} />}
      {showExtract && <ExtractModal docId={typeof showExtract === 'string' ? showExtract : ''} onExtract={runExtraction} extracting={extracting} onClose={() => setShowExtract(false)} isMobile={isMobile} />}
      {showEditBid && <EditBidModal bid={bid} onSubmit={updateBid} onClose={() => setShowEditBid(false)} isMobile={isMobile} />}
    </div>
  );
}

function mobileActionBtn(bg) {
  return { padding: '8px 14px', fontSize: 12, fontWeight: 600, background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', minHeight: 40, whiteSpace: 'nowrap' };
}

/* ═══════ TAB COMPONENTS ═══════ */

function OverviewTab({ bid, isMobile }) {
  const cardStyle = { background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '16px 20px', marginBottom: 12 };
  const labelStyle = { fontSize: 11, color: '#94a3b8', margin: '0 0 2px', textTransform: 'uppercase' };
  const valueStyle = { fontSize: 13, color: '#1e293b', margin: 0, fontWeight: 500 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Project Details</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <div><p style={labelStyle}>Project Name</p><p style={valueStyle}>{bid.project_name || '—'}</p></div>
          <div><p style={labelStyle}>Project Number</p><p style={valueStyle}>{bid.project_number || '—'}</p></div>
          <div><p style={labelStyle}>GC Project #</p><p style={valueStyle}>{bid.gc_project_number || '—'}</p></div>
          <div><p style={labelStyle}>Address</p><p style={valueStyle}>{[bid.project_address, bid.project_city, bid.project_state].filter(Boolean).join(', ') || '—'}</p></div>
          <div><p style={labelStyle}>Bid Source</p><p style={valueStyle}>{bid.bid_source || '—'}</p></div>
          <div><p style={labelStyle}>Invited By</p><p style={valueStyle}>{bid.invited_by || '—'}</p></div>
        </div>
      </div>
      <div style={cardStyle}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Bid Requirements</h3>
        <div style={{ display: 'grid', gap: 10 }}>
          <div><p style={labelStyle}>Due Date</p><p style={valueStyle}>{fmt(bid.bid_due_date)}{bid.bid_due_time ? ` at ${bid.bid_due_time}` : ''}</p></div>
          <div><p style={labelStyle}>Pre-Bid Meeting</p><p style={valueStyle}>{bid.pre_bid_meeting ? fmt(bid.pre_bid_meeting) : 'None'}{bid.pre_bid_required ? ' (Required)' : ''}</p></div>
          <div><p style={labelStyle}>Prevailing Wage</p><p style={valueStyle}>{bid.prevailing_wage ? 'Yes' : 'No'}</p></div>
          <div><p style={labelStyle}>Bond Required</p><p style={valueStyle}>{bid.bond_required ? 'Yes' : 'No'}</p></div>
          <div><p style={labelStyle}>Insurance</p><p style={valueStyle}>{bid.insurance_requirements || 'Standard'}</p></div>
          <div><p style={labelStyle}>Est. Start Date</p><p style={valueStyle}>{fmt(bid.estimated_start_date)}</p></div>
          <div><p style={labelStyle}>Est. Duration</p><p style={valueStyle}>{bid.estimated_duration || '—'}</p></div>
        </div>
      </div>
      <div style={{ ...cardStyle, gridColumn: isMobile ? '1' : '1 / -1' }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Scope Description</h3>
        <p style={{ fontSize: 13, color: '#475569', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{bid.scope_description || 'No scope description provided.'}</p>
      </div>
      {bid.submitted_amount && (
        <div style={cardStyle}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Submission</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><p style={labelStyle}>Submitted Amount</p><p style={{ ...valueStyle, fontSize: 18, fontWeight: 800 }}>{money(bid.submitted_amount)}</p></div>
            <div><p style={labelStyle}>Submission Date</p><p style={valueStyle}>{fmt(bid.submission_date)}</p></div>
            <div><p style={labelStyle}>Method</p><p style={valueStyle}>{bid.submission_method || '—'}</p></div>
          </div>
        </div>
      )}
      {bid.bid_status === 'awarded' && (
        <div style={{ ...cardStyle, borderColor: '#16a34a' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#16a34a', margin: '0 0 8px' }}>Award Details</h3>
          <div><p style={labelStyle}>Awarded Amount</p><p style={{ ...valueStyle, fontSize: 18, fontWeight: 800, color: '#16a34a' }}>{money(bid.awarded_amount)}</p></div>
          <div><p style={labelStyle}>Award Date</p><p style={valueStyle}>{fmt(bid.award_date)}</p></div>
        </div>
      )}
      {bid.bid_status === 'lost' && (
        <div style={{ ...cardStyle, borderColor: '#ef4444' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#ef4444', margin: '0 0 8px' }}>Lost Bid Details</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            <div><p style={labelStyle}>Reason</p><p style={valueStyle}>{bid.lost_reason || '—'}</p></div>
            <div><p style={labelStyle}>Lost To</p><p style={valueStyle}>{bid.lost_to_competitor || '—'}</p></div>
            {bid.lost_amount && <div><p style={labelStyle}>Winning Amount</p><p style={valueStyle}>{money(bid.lost_amount)}</p></div>}
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ documents, onAdd, onDelete, isMobile }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>Documents</h3>
        <button data-testid="add-doc-btn" onClick={onAdd}
          style={{ padding: '8px 14px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
          + Add Document
        </button>
      </div>
      {documents.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          No documents uploaded yet. Add plans, specifications, or addenda.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {documents.map(doc => (
            <div key={doc.id} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 8 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{doc.name || doc.original_filename || 'Unnamed'}</span>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}</span>
                  {doc.ai_extracted && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>AI EXTRACTED</span>}
                </div>
                {doc.description && <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{doc.description}</p>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {doc.file_url && <a href={doc.file_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#3b82f6', minHeight: 36, display: 'flex', alignItems: 'center' }}>View</a>}
                <button onClick={() => onDelete(doc.id)} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', minHeight: 36 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExtractionTab({ bid, documents, onExtract, extracting, isMobile }) {
  const extractions = bid.ai_extractions || [];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>AI Document Extraction</h3>
      </div>
      <div style={{ background: '#fffbeb', borderRadius: 8, border: '1px solid #fbbf24', padding: '12px 16px', marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0, fontWeight: 500 }}>
          AI extraction labels each field with confidence: <span style={{ color: CONFIDENCE_COLORS.CONFIRMED_FROM_DOCUMENT, fontWeight: 700 }}>CONFIRMED</span>,{' '}
          <span style={{ color: CONFIDENCE_COLORS.INFERRED, fontWeight: 700 }}>INFERRED</span>, or{' '}
          <span style={{ color: CONFIDENCE_COLORS.MISSING_NEEDS_REVIEW, fontWeight: 700 }}>MISSING / NEEDS REVIEW</span>.
          AI never calculates pricing. All pricing must be entered manually.
        </p>
      </div>
      {documents.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>Select a document to extract from, or paste text:</p>
          {documents.map(doc => (
            <button key={doc.id} onClick={() => onExtract(doc.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: doc.ai_extracted ? '#f0fdf4' : '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 6, cursor: 'pointer', fontSize: 13, minHeight: 44 }}>
              {doc.name || doc.original_filename} — {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
              {doc.ai_extracted && <span style={{ marginLeft: 8, fontSize: 10, color: '#16a34a', fontWeight: 600 }}>EXTRACTED</span>}
            </button>
          ))}
        </div>
      )}
      {extractions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          No extractions yet. Upload documents and run AI extraction.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {extractions.map((ext, i) => (
            <div key={ext.id || i} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>Extraction #{extractions.length - i}</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmt(ext.extracted_at)} — {ext.model}</span>
              </div>
              {ext.data && !ext.data.error ? (
                <ExtractionDisplay data={ext.data} isMobile={isMobile} />
              ) : (
                <p style={{ fontSize: 13, color: '#ef4444' }}>{ext.data?.error || 'Extraction failed'}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExtractionDisplay({ data, isMobile }) {
  const renderField = (label, field) => {
    if (!field) return null;
    const val = typeof field === 'object' ? field.value : field;
    const conf = typeof field === 'object' ? field.confidence : null;
    if (!val && val !== false) return null;
    return (
      <div key={label} style={{ marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{label}: </span>
        <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : val}</span>
        {conf && <span style={{ fontSize: 9, marginLeft: 6, padding: '1px 5px', borderRadius: 3, background: `${CONFIDENCE_COLORS[conf] || '#94a3b8'}18`, color: CONFIDENCE_COLORS[conf] || '#94a3b8', fontWeight: 700 }}>{conf.replace(/_/g, ' ')}</span>}
      </div>
    );
  };

  return (
    <div>
      {data.project_info && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 6px', textTransform: 'uppercase' }}>Project Info</h4>
          {Object.entries(data.project_info).map(([k, v]) => renderField(k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), v))}
        </div>
      )}
      {data.bid_requirements && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 6px', textTransform: 'uppercase' }}>Bid Requirements</h4>
          {Object.entries(data.bid_requirements).map(([k, v]) => renderField(k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), v))}
        </div>
      )}
      {data.scope_items && data.scope_items.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 6px', textTransform: 'uppercase' }}>Scope Items</h4>
          {data.scope_items.map((item, i) => (
            <div key={i} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: 6, marginBottom: 6 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', margin: '0 0 4px' }}>{item.division || `Item ${i + 1}`}</p>
              <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>{item.description}</p>
              {item.quantities && renderField('Quantities', item.quantities)}
              {item.materials && renderField('Materials', item.materials)}
            </div>
          ))}
        </div>
      )}
      {data.missing_information && data.missing_information.length > 0 && (
        <div style={{ background: '#fef2f2', borderRadius: 6, padding: '10px 14px' }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', margin: '0 0 6px' }}>Missing Information</h4>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#991b1b', fontSize: 12 }}>
            {data.missing_information.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {data.extraction_summary && (
        <p style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>{data.extraction_summary}</p>
      )}
    </div>
  );
}

function TakeoffTab({ bid, onUpdate, isMobile }) {
  const [takeoff, setTakeoff] = useState(bid.takeoff_status || 'not_started');
  const [pricing, setPricing] = useState(bid.pricing_status || 'not_started');
  const [notes, setNotes] = useState(bid.pricing_notes || '');
  const [saved, setSaved] = useState(false);

  const save = async () => {
    await onUpdate({ takeoff_status: takeoff, pricing_status: pricing, pricing_notes: notes });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const statuses = ['not_started', 'in_progress', 'complete', 'handed_off'];
  const statusLabels = { not_started: 'Not Started', in_progress: 'In Progress', complete: 'Complete', handed_off: 'Handed Off' };

  return (
    <div>
      <div style={{ background: '#fffbeb', borderRadius: 8, border: '1px solid #fbbf24', padding: '12px 16px', marginBottom: 16 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0, fontWeight: 600 }}>
          MANUAL PRICING REQUIRED — AI does not calculate or invent pricing. All pricing must be manually entered by estimators.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Takeoff Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {statuses.map(s => (
              <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#475569', padding: '6px 0' }}>
                <input type="radio" name="takeoff" checked={takeoff === s} onChange={() => setTakeoff(s)} />
                {statusLabels[s]}
              </label>
            ))}
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Pricing Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {statuses.map(s => (
              <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#475569', padding: '6px 0' }}>
                <input type="radio" name="pricing" checked={pricing === s} onChange={() => setPricing(s)} />
                {statusLabels[s]}
              </label>
            ))}
          </div>
        </div>
      </div>
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 20px', marginTop: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Pricing Notes</h3>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          rows={4} placeholder="Notes about pricing, materials, subcontractor quotes, exclusions..."
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
      </div>
      <button onClick={save} data-testid="save-takeoff-btn"
        style={{ marginTop: 12, padding: '10px 24px', background: saved ? '#16a34a' : '#1e293b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
        {saved ? 'Saved!' : 'Save Takeoff & Pricing'}
      </button>
    </div>
  );
}

function ClarificationsTab({ clarifications, onAdd, bidId, onReload, isMobile }) {
  const pending = clarifications.filter(c => c.status === 'pending');
  const answered = clarifications.filter(c => c.status === 'answered');

  const answerRfi = async (id, response) => {
    await fetch(`${API}/api/sales/commercial/bids/clarifications/${id}`, authOpts({
      method: 'PUT', body: JSON.stringify({ response }),
    }));
    onReload();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>Clarifications / RFIs</h3>
        <button data-testid="add-rfi-btn" onClick={onAdd}
          style={{ padding: '8px 14px', background: '#ec4899', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
          + Add RFI
        </button>
      </div>
      {pending.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', margin: '0 0 8px', textTransform: 'uppercase' }}>Pending ({pending.length})</h4>
          {pending.map(c => (
            <RfiCard key={c.id} rfi={c} onAnswer={answerRfi} isMobile={isMobile} />
          ))}
        </div>
      )}
      {answered.length > 0 && (
        <div>
          <h4 style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', margin: '0 0 8px', textTransform: 'uppercase' }}>Answered ({answered.length})</h4>
          {answered.map(c => <RfiCard key={c.id} rfi={c} isMobile={isMobile} />)}
        </div>
      )}
      {clarifications.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          No clarifications or RFIs yet.
        </div>
      )}
    </div>
  );
}

function RfiCard({ rfi, onAnswer, isMobile }) {
  const [response, setResponse] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{rfi.rfi_number ? `RFI #${rfi.rfi_number}` : 'RFI'}</span>
        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: rfi.status === 'pending' ? '#fef3c7' : '#dcfce7', color: rfi.status === 'pending' ? '#92400e' : '#166534', fontWeight: 600 }}>
          {rfi.status === 'pending' ? 'PENDING' : 'ANSWERED'}
        </span>
      </div>
      <p style={{ fontSize: 13, color: '#1e293b', margin: '0 0 4px', fontWeight: 500 }}>Q: {rfi.question}</p>
      {rfi.response && <p style={{ fontSize: 13, color: '#16a34a', margin: '4px 0 0' }}>A: {rfi.response}</p>}
      {rfi.status === 'pending' && onAnswer && (
        <div style={{ marginTop: 8 }}>
          {showAnswer ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={response} onChange={e => setResponse(e.target.value)} placeholder="Enter response..."
                style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, minHeight: 36 }} />
              <button onClick={() => { onAnswer(rfi.id, response); setShowAnswer(false); }}
                style={{ padding: '8px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
                Save
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAnswer(true)}
              style={{ fontSize: 12, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              + Add Response
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryTab({ bid, isMobile }) {
  const history = [...(bid.status_history || [])].reverse();
  return (
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Status History</h3>
      {history.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>No history available.</p>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 20 }}>
          <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
          {history.map((h, i) => (
            <div key={i} style={{ marginBottom: 16, position: 'relative' }}>
              <div style={{ position: 'absolute', left: -16, top: 4, width: 12, height: 12, borderRadius: '50%', background: STATUS_COLORS[h.status] || '#94a3b8', border: '2px solid #fff' }} />
              <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '10px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: STATUS_COLORS[h.status] || '#1e293b' }}>{STATUS_LABELS[h.status] || h.status}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmt(h.changed_at)}</span>
                </div>
                {h.notes && <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>{h.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════ MODALS ═══════ */

function ModalWrapper({ title, onClose, children, isMobile }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto', padding: isMobile ? '20px 16px' : '24px 28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: '#1e293b' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function StatusChangeModal({ bid, onSubmit, onClose, isMobile }) {
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const allStatuses = [...BID_PIPELINE, ...TERMINAL];
  return (
    <ModalWrapper title="Change Bid Status" onClose={onClose} isMobile={isMobile}>
      <select value={status} onChange={e => setStatus(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44 }}>
        <option value="">Select new status...</option>
        {allStatuses.filter(s => s !== bid.bid_status).map(s => (
          <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
        ))}
      </select>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)..."
        rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, resize: 'vertical', boxSizing: 'border-box' }} />
      <button onClick={() => status && onSubmit(status, notes)} disabled={!status}
        style={{ width: '100%', padding: '12px', background: status ? '#1e293b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
        Update Status
      </button>
    </ModalWrapper>
  );
}

function SubmitBidModal({ bid, onSubmit, onClose, isMobile }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('email');
  const [notes, setNotes] = useState('');
  return (
    <ModalWrapper title="Submit Bid" onClose={onClose} isMobile={isMobile}>
      <div style={{ background: '#fef3c7', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0, fontWeight: 500 }}>Enter the manually calculated bid amount below.</p>
      </div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Submitted Amount ($) *</label>
      <input data-testid="submit-amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, marginBottom: 12, minHeight: 44, boxSizing: 'border-box' }} />
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Submission Method</label>
      <select value={method} onChange={e => setMethod(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44 }}>
        <option value="email">Email</option>
        <option value="online_portal">Online Portal</option>
        <option value="hand_delivered">Hand Delivered</option>
        <option value="fax">Fax</option>
        <option value="other">Other</option>
      </select>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Submission notes..."
        rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, resize: 'vertical', boxSizing: 'border-box' }} />
      <button onClick={() => amount && onSubmit({ submitted_amount: parseFloat(amount), submission_method: method, submission_notes: notes })} disabled={!amount}
        style={{ width: '100%', padding: '12px', background: amount ? '#14b8a6' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
        Submit Bid
      </button>
    </ModalWrapper>
  );
}

function AwardModal({ onSubmit, onClose, isMobile }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  return (
    <ModalWrapper title="Bid Awarded" onClose={onClose} isMobile={isMobile}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Awarded Amount ($)</label>
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, marginBottom: 12, minHeight: 44, boxSizing: 'border-box' }} />
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Award Date</label>
      <input type="date" value={date} onChange={e => setDate(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44, boxSizing: 'border-box' }} />
      <button onClick={() => onSubmit({ awarded_amount: amount ? parseFloat(amount) : null, award_date: date })}
        style={{ width: '100%', padding: '12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
        Mark as Awarded
      </button>
    </ModalWrapper>
  );
}

function LoseModal({ onSubmit, onClose, isMobile }) {
  const [reason, setReason] = useState('');
  const [competitor, setCompetitor] = useState('');
  const [amount, setAmount] = useState('');
  return (
    <ModalWrapper title="Bid Lost" onClose={onClose} isMobile={isMobile}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Reason Lost *</label>
      <select value={reason} onChange={e => setReason(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44 }}>
        <option value="">Select reason...</option>
        <option value="price_too_high">Price Too High</option>
        <option value="scope_mismatch">Scope Mismatch</option>
        <option value="schedule_conflict">Schedule Conflict</option>
        <option value="went_with_incumbent">Went With Incumbent</option>
        <option value="project_cancelled">Project Cancelled</option>
        <option value="no_response">No Response / Ghosted</option>
        <option value="bond_issue">Bond / Insurance Issue</option>
        <option value="other">Other</option>
      </select>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Lost To (Competitor)</label>
      <input value={competitor} onChange={e => setCompetitor(e.target.value)} placeholder="Competitor name..."
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44, boxSizing: 'border-box' }} />
      <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Winning Amount ($)</label>
      <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="If known..."
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, marginBottom: 12, minHeight: 44, boxSizing: 'border-box' }} />
      <button onClick={() => reason && onSubmit({ lost_reason: reason, lost_to_competitor: competitor, lost_amount: amount ? parseFloat(amount) : null })} disabled={!reason}
        style={{ width: '100%', padding: '12px', background: reason ? '#ef4444' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
        Mark as Lost
      </button>
    </ModalWrapper>
  );
}

function AddDocModal({ onSubmit, onClose, isMobile }) {
  const [name, setName] = useState('');
  const [docType, setDocType] = useState('plans');
  const [description, setDescription] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [version, setVersion] = useState('1');
  return (
    <ModalWrapper title="Add Document" onClose={onClose} isMobile={isMobile}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Document Name *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Architectural Plans Rev A"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Type</label>
          <select value={docType} onChange={e => setDocType(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44 }}>
            {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>File URL</label>
          <input value={fileUrl} onChange={e => setFileUrl(e.target.value)} placeholder="https://..."
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Version</label>
            <input value={version} onChange={e => setVersion(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <button onClick={() => name && onSubmit({ name, doc_type: docType, description, file_url: fileUrl, version })} disabled={!name}
          style={{ width: '100%', padding: '12px', background: name ? '#6366f1' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
          Add Document
        </button>
      </div>
    </ModalWrapper>
  );
}

function AddRfiModal({ onSubmit, onClose, isMobile }) {
  const [number, setNumber] = useState('');
  const [question, setQuestion] = useState('');
  const [priority, setPriority] = useState('normal');
  return (
    <ModalWrapper title="Add Clarification / RFI" onClose={onClose} isMobile={isMobile}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>RFI #</label>
            <input value={number} onChange={e => setNumber(e.target.value)} placeholder="001"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44 }}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Question / Clarification *</label>
          <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={3}
            placeholder="What needs to be clarified?"
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <button onClick={() => question && onSubmit({ rfi_number: number, question, priority })} disabled={!question}
          style={{ width: '100%', padding: '12px', background: question ? '#ec4899' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
          Add RFI
        </button>
      </div>
    </ModalWrapper>
  );
}

function ExtractModal({ docId, onExtract, extracting, onClose, isMobile }) {
  const [text, setText] = useState('');
  return (
    <ModalWrapper title="AI Document Extraction" onClose={onClose} isMobile={isMobile}>
      <div style={{ background: '#fef3c7', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>
          Paste the document text below. AI will extract project data with confidence labels. AI NEVER generates pricing.
        </p>
      </div>
      <textarea data-testid="extract-text-input" value={text} onChange={e => setText(e.target.value)} rows={8}
        placeholder="Paste document text here (plans summary, bid invitation, scope of work)..."
        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', marginBottom: 12, boxSizing: 'border-box' }} />
      <button data-testid="run-extraction-btn" onClick={() => text && onExtract(docId, text)} disabled={!text || extracting}
        style={{ width: '100%', padding: '12px', background: text && !extracting ? '#f59e0b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
        {extracting ? 'Extracting...' : 'Run AI Extraction'}
      </button>
    </ModalWrapper>
  );
}

function EditBidModal({ bid, onSubmit, onClose, isMobile }) {
  const [form, setForm] = useState({
    project_name: bid.project_name || '',
    project_number: bid.project_number || '',
    gc_project_number: bid.gc_project_number || '',
    project_address: bid.project_address || '',
    bid_due_date: bid.bid_due_date || '',
    bid_due_time: bid.bid_due_time || '',
    scope_description: bid.scope_description || '',
    prevailing_wage: bid.prevailing_wage || false,
    bond_required: bid.bond_required || false,
    notes: bid.notes || '',
  });

  return (
    <ModalWrapper title="Edit Bid" onClose={onClose} isMobile={isMobile}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Project Name</label>
          <input value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))}
            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Bid Due Date</label>
            <input type="date" value={form.bid_due_date} onChange={e => setForm(f => ({ ...f, bid_due_date: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Bid Due Time</label>
            <input type="time" value={form.bid_due_time} onChange={e => setForm(f => ({ ...f, bid_due_time: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 44, boxSizing: 'border-box' }} />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Scope Description</label>
          <textarea value={form.scope_description} onChange={e => setForm(f => ({ ...f, scope_description: e.target.value }))}
            rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={form.prevailing_wage} onChange={e => setForm(f => ({ ...f, prevailing_wage: e.target.checked }))} /> Prevailing Wage
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={form.bond_required} onChange={e => setForm(f => ({ ...f, bond_required: e.target.checked }))} /> Bond Required
          </label>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <button onClick={() => onSubmit(form)}
          style={{ width: '100%', padding: '12px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', minHeight: 48 }}>
          Save Changes
        </button>
      </div>
    </ModalWrapper>
  );
}
